import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext, getIn, Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FarmRiskType } from '@ourbranch/lookups';
import { getDefaultFarmCoverageD } from '@ourbranch/farm-utils';

import { Button } from 'core/components/button';
import Field from 'core/components/form/form.v2';
import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { useToast } from 'core/components/toast';
import { appConfig } from 'brand-config';
import { useFarmPolicyContext } from 'common/hooks/use-farm-policy-context';
import {
  getPropertyTypeOptions,
  getMaskedResidenceUsageOptions,
  resetFarmRiskBuildingOnTypeChange
} from 'common/helpers/farm-helpers';
import { useClearInvalidRiskOptions } from 'common/helpers/use-clear-invalid-risk-options';

import useStyles from './edit-risk-modal.styles';

// Inner component to use the clearing hook (hooks can't be called inside render props)
const EditRiskFormFields = ({ formValues, currentFormType, classes, handleSubmit, isValid, locationState }) => {
  const { setFieldValue } = useFormikContext();
  // #32207: route-aware product so Home (Z) residence-usage restrictions (e.g. no Tenant Occupied
  // Rental) apply when editing a risk on the policy route (the offer store is empty there).
  const { farmPolicyType } = useFarmPolicyContext();
  const formType = formValues.type;

  // Clear invalid propertyType / residenceUsage when risk type changes (issue #30827).
  useClearInvalidRiskOptions({
    riskType: formType,
    propertyType: formValues.propertyType,
    residenceUsage: formValues.residenceUsage,
    setFieldValue,
    propertyTypePath: 'propertyType',
    residenceUsagePath: 'residenceUsage'
  });

  const isDwellingRiskType = [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3].includes(formType);

  return (
    <Form>
      <Field xs={12} type="string" name="description" id="description" label="Risk name" mode="light" />
      <Field
        xs={12}
        type="select"
        name="type"
        id="type"
        mode="light"
        label="Dwelling risk form type"
        options={riskTypeOptionsLookup[currentFormType]}
      />
      {isDwellingRiskType && (
        <>
          <Field
            key={`propertyType-${formType}`}
            xs={12}
            type="select"
            name="propertyType"
            id="propertyType"
            mode="light"
            label="Structure Category"
            options={getPropertyTypeOptions(formType)}
          />
          <Field
            key={`residenceUsage-${formType}`}
            xs={12}
            type="select"
            name="residenceUsage"
            id="residenceUsage"
            mode="light"
            label="Residence Type/Usage"
            // Apply the policy-type mask so e.g. Home (Z) doesn't offer Tenant
            // Occupied Rental (GitHub issue #31691)
            options={getMaskedResidenceUsageOptions(formType, farmPolicyType, locationState)}
          />
        </>
      )}
      <div className={classes.buttonContainer}>
        <Button
          variant="contained"
          type="submit"
          color="primary"
          onClick={handleSubmit}
          className={classes.buttons}
          disabled={!isValid}
        >
          Save changes
        </Button>
      </div>
    </Form>
  );
};

const riskTypeOptionsLookup = {
  FO1: [
    { id: FarmRiskType.Fo1, value: 'FO-1 Basic Form' },
    { id: FarmRiskType.Fo2, value: 'FO-2 Broad Form' },
    { id: FarmRiskType.Fo3, value: 'FO-3 Special Form' }
  ],
  FO2: [
    { id: FarmRiskType.Fo1, value: 'FO-1 Basic Form' },
    { id: FarmRiskType.Fo2, value: 'FO-2 Broad Form' },
    { id: FarmRiskType.Fo3, value: 'FO-3 Special Form' }
  ],
  FO3: [
    { id: FarmRiskType.Fo1, value: 'FO1 Basic Form' },
    { id: FarmRiskType.Fo2, value: 'FO2 Broad Form' },
    { id: FarmRiskType.Fo3, value: 'FO3 Special Form' }
  ],
  FO4: [{ id: FarmRiskType.Fo4, value: 'FO-4 Renters Form' }],
  // FO5: [], will need to add FO5 when we add it
  FO6: [
    { id: FarmRiskType.Fo6Structures, value: 'FO-6 Structures Form' },
    { id: FarmRiskType.Fo6Scheduled, value: 'FO-6 Scheduled Form' },
    { id: FarmRiskType.Fo6Unscheduled, value: 'FO-6 Unscheduled Form' }
  ]
};

const EditRiskModal = ({ onClose, fieldName }) => {
  const classes = useStyles();
  const toast = useToast();
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  // #32207: route-aware state fallback (policy store on the policy route where the offer store is empty).
  const { state: offerState } = useFarmPolicyContext();

  const currentFormType = getIn(values, `${fieldName}.type`);
  const currentPropertyType = getIn(values, `${fieldName}.building.propertyType`);
  const currentResidenceUsage = getIn(values, `${fieldName}.building.residenceUsage`);
  // The fieldName is `farmLocations[N].risks[M]`; read the owning location's state for
  // mask-aware residence usage filtering, falling back to the offer's state when the
  // location hasn't been geocoded yet (#31691).
  const locationFieldName = fieldName?.replace(/\.risks\[\d+\]$/, '');
  const locationState =
    (locationFieldName ? getIn(values, `${locationFieldName}.address.state`) : undefined) || offerState;

  const validationSchema = Yup.object().shape({
    description: Yup.string().required('Risk name is required'),
    type: Yup.string().required('Dwelling risk form type is required'),
    propertyType: Yup.string()
      .when('type', {
        is: (type) => [FarmRiskType.Fo3].includes(type),
        then: Yup.string().required('Structure category is required'),
        otherwise: Yup.string()
      })
      .test('no-mobile-home-for-fo3', 'Mobile Home is not allowed for FO-3', function (value) {
        const { type } = this.parent;
        return !(type === FarmRiskType.Fo3 && value === 'MH');
      }),
    residenceUsage: Yup.string()
      .when(['type', 'propertyType'], {
        is: (type, propertyType) => {
          if (![FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3].includes(type)) {
            return false;
          }

          // Barndominium ('B') doesn't require residence usage
          // Mobile Home ('MH') is not allowed for FO-3 (enforced by propertyType validation)
          if (type === FarmRiskType.Fo3 && propertyType === 'B') {
            return false;
          }

          return true;
        },
        then: Yup.string().required('Residence type/usage is required'),
        otherwise: Yup.string()
      })
      .test('no-vacant-for-fo2-fo3', 'Vacant is not allowed for FO-2/FO-3', function (value) {
        const { type } = this.parent;
        return !([FarmRiskType.Fo2, FarmRiskType.Fo3].includes(type) && value === 'V');
      })
  });
  const initialValues = {
    description: getIn(values, `${fieldName}.description`),
    type: currentFormType,
    propertyType: currentPropertyType,
    residenceUsage: currentResidenceUsage
  };

  const handleSubmit = async (modalValues) => {
    const originalFormType = currentFormType;
    const newFormType = modalValues.type;
    const riskName = modalValues.description;

    setFieldValue(`${fieldName}.description`, modalValues.description);
    setFieldTouched(`${fieldName}.description`, true);
    setFieldValue(`${fieldName}.type`, modalValues.type);
    setFieldTouched(`${fieldName}.type`, true);

    // #32514 reopen: if the edit crosses a form family (dwelling ↔ renters ↔ structures), the prior
    // type's building + coverages no longer apply and would leak incompatible fields into the rater
    // ("farm rating error"). Reset them before the type-specific defaults below repopulate the new
    // form. This is a no-op for the same-family dwelling switches the modal currently offers (its
    // riskTypeOptionsLookup only lists within-family options), so existing FO-1/2/3 edits are
    // unaffected; it hardens the modal against the same stale-data class of bug on any future
    // cross-family option. The propertyType/residenceUsage sets below run after the reset and survive.
    resetFarmRiskBuildingOnTypeChange({
      fromType: originalFormType,
      toType: newFormType,
      riskPath: fieldName,
      setFieldValue,
      setFieldTouched
    });

    // FO-4 always uses 'FD' (Home or Rental Unit) and 'C' (Contents Only) — GitHub Issue: #29115
    if (modalValues.type === FarmRiskType.Fo4) {
      setFieldValue(`${fieldName}.building.propertyType`, 'FD');
      setFieldTouched(`${fieldName}.building.propertyType`, true);
      setFieldValue(`${fieldName}.building.residenceUsage`, 'C');
      setFieldTouched(`${fieldName}.building.residenceUsage`, true);
    } else {
      // Issue #30827: persist Structure Category edits made in the modal.
      setFieldValue(`${fieldName}.building.propertyType`, modalValues.propertyType);
      setFieldTouched(`${fieldName}.building.propertyType`, true);
      setFieldValue(`${fieldName}.building.residenceUsage`, modalValues.residenceUsage);
      setFieldTouched(`${fieldName}.building.residenceUsage`, true);
    }

    if (originalFormType !== newFormType) {
      // Automatically adjust Coverage D when switching between FO types to ensure compliance with rules
      const coverageA = getIn(values, `${fieldName}.coverages.coverageA`);
      const lossSettlement = getIn(values, `${fieldName}.coverages.lossSettlement`);
      const residenceUsage = modalValues.residenceUsage;

      if ([FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3].includes(newFormType)) {
        // Coverage D defaults: P/S = 20% of CovA, R = 10%, V = $0 — read from brand
        // config of the *target* form type. GitHub issues #29958, #30548.
        if (residenceUsage === 'V') {
          setFieldValue(`${fieldName}.coverages.coverageD`, 0);
          setFieldTouched(`${fieldName}.coverages.coverageD`, true);
        } else if (['P', 'S', 'R'].includes(residenceUsage) && coverageA) {
          // The fieldName is `farmLocations[N].risks[M]`; strip the `.risks[M]` suffix
          // to read the location's state for the brand-config lookup.
          const locationFieldName = fieldName.replace(/\.risks\[\d+\]$/, '');
          if (locationFieldName === fieldName) {
            // Regex didn't match — fieldName format has drifted from the expected
            // `farmLocations[N].risks[M]` shape. We'd silently fall through to the
            // legacy 20% default; warn so we catch the drift early in dev/staging.
            // eslint-disable-next-line no-console
            console.warn('[edit-risk-modal] Could not extract location path from fieldName:', fieldName);
          }
          const state = getIn(values, `${locationFieldName}.address.state`);
          const businessRulesD = appConfig.farm?.[state]?.risks?.[newFormType]?.businessRules?.coverageD;
          const calculatedCoverageD = getDefaultFarmCoverageD({
            residenceUsage,
            lossSettlement,
            coverageA,
            businessRulesD
          });
          setFieldValue(`${fieldName}.coverages.coverageD`, calculatedCoverageD);
          setFieldTouched(`${fieldName}.coverages.coverageD`, true);
        }
      }

      const newFormLabel =
        riskTypeOptionsLookup[originalFormType]?.find((option) => option.id === newFormType)?.value || newFormType;

      toast.notify({
        type: 'success',
        message: `We've updated ${riskName} to ${newFormLabel}. Please review property details and coverages for accuracy.`
      });
    }

    onClose();
  };

  return (
    <BaseDialog className={classes.modal} onClose={onClose} open>
      <div className={classes.container}>
        <Label className={classes.mainLabel} type="green75Big">
          Modify dwelling or structure risk
        </Label>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ handleSubmit, isValid, values: formValues }) => (
            <EditRiskFormFields
              formValues={formValues}
              currentFormType={currentFormType}
              classes={classes}
              handleSubmit={handleSubmit}
              isValid={isValid}
              locationState={locationState}
            />
          )}
        </Formik>
      </div>
    </BaseDialog>
  );
};

EditRiskModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  fieldName: PropTypes.string
};

export default observer(EditRiskModal);
